import { normalizeFacilityLabel } from "@/features/hotels/utils/facilities/normalization";
import type {
  FacilityCategoryKey,
  FacilityCategoryMeta,
  FacilityTaxonomyItem,
} from "@/features/hotels/utils/facilities/types";

export const FACILITY_CATEGORIES: Record<
  FacilityCategoryKey,
  FacilityCategoryMeta
> = {
  "core-essentials": {
    key: "core-essentials",
    label: "Core Essentials",
  },
  accessibility: {
    key: "accessibility",
    label: "Accessibility",
  },
  safety: { key: "safety", label: "Safety" },
  leisure: { key: "leisure", label: "Leisure" },
  dining: { key: "dining", label: "Dining" },
  service: { key: "service", label: "Service" },
  sports: { key: "sports", label: "Sports" },
  "core-support": {
    key: "core-support",
    label: "Core Support",
  },
  wellness: { key: "wellness", label: "Wellness" },
  family: { key: "family", label: "Family" },
  business: { key: "business", label: "Business" },
  entertainment: {
    key: "entertainment",
    label: "Entertainment",
  },
  secondary: { key: "secondary", label: "Secondary" },
  policies: { key: "policies", label: "Policies" },
  admin: { key: "admin", label: "Admin" },
  extra: { key: "extra", label: "Extra" },
};

export const FACILITY_CATEGORY_ORDER: FacilityCategoryKey[] = [
  "core-essentials",
  "accessibility",
  "safety",
  "leisure",
  "dining",
  "service",
  "sports",
  "core-support",
  "wellness",
  "family",
  "business",
  "entertainment",
  "secondary",
  "policies",
  "admin",
  "extra",
];

const createTaxonomyItem = (
  item: Omit<FacilityTaxonomyItem, "normalizedLabel">,
): FacilityTaxonomyItem => ({
  ...item,
  normalizedLabel: normalizeFacilityLabel(item.label),
});

export const FACILITY_TAXONOMY: FacilityTaxonomyItem[] = [
  createTaxonomyItem({
    key: "wifi",
    label: "Wi-fi",
    rank: 1,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "air-conditioning-public-areas",
    label: "Air conditioning in public areas",
    rank: 2,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "24-hour-reception",
    label: "24-hour reception",
    rank: 3,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "towels-and-bed-linen",
    label: "Towels and bed linen",
    rank: 4,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "lift-access",
    label: "Lift access",
    rank: 5,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "car-park",
    label: "Car park",
    rank: 6,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "secure-parking",
    label: "Secure parking",
    rank: 7,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "valet-parking",
    label: "Valet parking",
    rank: 8,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "wheelchair-accessible",
    label: "Wheelchair-accessible",
    rank: 9,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "room-service",
    label: "Room service",
    rank: 10,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "laundry-service",
    label: "Laundry service",
    rank: 11,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "luggage-room",
    label: "Luggage room",
    rank: 12,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "airport-shuttle",
    label: "Airport Shuttle",
    rank: 13,
    categoryKey: "accessibility",
  }),
  createTaxonomyItem({
    key: "car-hire",
    label: "Car hire",
    rank: 15,
    categoryKey: "accessibility",
  }),
  createTaxonomyItem({
    key: "gym",
    label: "Gym",
    rank: 23,
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "restaurant",
    label: "Restaurant",
    rank: 34,
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "swimming-pool",
    label: "Outdoor freshwater pool",
    rank: 29,
    categoryKey: "leisure",
  }),
  createTaxonomyItem({
    key: "breakfast",
    label: "Breakfast room",
    rank: 35,
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "multilingual-staff",
    label: "Multilingual staff",
    rank: 17,
    categoryKey: "accessibility",
  }),
  createTaxonomyItem({
    key: "24-hour-security",
    label: "24-hour security",
    rank: 19,
    categoryKey: "safety",
  }),
  createTaxonomyItem({
    key: "spa-centre",
    label: "Spa centre",
    rank: 24,
    categoryKey: "wellness",
  }),
  createTaxonomyItem({
    key: "sauna",
    label: "Sauna",
    rank: 25,
    categoryKey: "wellness",
  }),
  createTaxonomyItem({
    key: "bar",
    label: "Bar",
    rank: 36,
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "concierge",
    label: "Concierge",
    rank: 40,
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "babysitting-service",
    label: "Babysitting service",
    rank: 45,
    categoryKey: "family",
  }),
  createTaxonomyItem({
    key: "children-playground",
    label: "Children playground",
    rank: 46,
    categoryKey: "family",
  }),
  createTaxonomyItem({
    key: "business-centre",
    label: "Business centre",
    rank: 49,
    categoryKey: "business",
  }),
  createTaxonomyItem({
    key: "meeting-room",
    label: "Meeting room",
    rank: 50,
    categoryKey: "business",
  }),
  createTaxonomyItem({
    key: "casino",
    label: "Casino",
    rank: 58,
    categoryKey: "entertainment",
  }),
  createTaxonomyItem({
    key: "nightclub",
    label: "Nightclub",
    rank: 59,
    categoryKey: "entertainment",
  }),
  createTaxonomyItem({
    key: "electric-kettle",
    label: "Electric kettle",
    rank: 71,
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "garage",
    label: "Garage",
    rank: 81,
    categoryKey: "core-support",
  }),
  createTaxonomyItem({
    key: "tennis",
    label: "Tennis",
    rank: 106,
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "small-pets-allowed",
    label: "Small pets allowed",
    rank: 117,
    categoryKey: "policies",
  }),
  createTaxonomyItem({
    key: "check-in-check-out-key-collection",
    label: "Check-in / Check-out / Key Collection",
    rank: 120,
    categoryKey: "admin",
  }),
];

export const facilityTaxonomyByNormalizedLabel = new Map(
  FACILITY_TAXONOMY.map((item) => [item.normalizedLabel, item]),
);

export const MAX_COMMON_FACILITIES = 4;
export const COMMON_FACILITY_ICON_KEYS = new Set<string>([
  "wifi",
  "air-conditioning-public-areas",
  "24-hour-reception",
  "towels-and-bed-linen",
  "lift-access",
  "car-park",
  "secure-parking",
  "valet-parking",
  "wheelchair-accessible",
  "room-service",
  "laundry-service",
  "luggage-room",
  "airport-shuttle",
  "car-hire",
  "gym",
  "restaurant",
  "swimming-pool",
  "breakfast",
]);
export const COMMON_FACILITY_PRIORITY = [
  "air-conditioning-public-areas",
  "restaurant",
  "swimming-pool",
  "24-hour-reception",
] as const;
export const COMMON_FACILITY_PRIORITY_SET = new Set<string>(
  COMMON_FACILITY_PRIORITY,
);
